'use client';

import './card.scss';

import * as z from 'zod';
import Link from 'next/link';

import { Badge } from '@sola/ark-core/atoms/badge/badge.tsx';
import { Skeleton } from '@sola/ark-core/atoms/skeleton/skeleton.tsx';
import { cn } from '@sola/ark-core/utils/helpers.ts';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';
import { IconNames } from '@sola/schema/lib/icons/icon-names.ts';
import { BadgeColors } from '@sola/schema/lib/schema.ts';
import { trackClick } from '@sola/tracking/lib/analytics/events.ts';
import { getElapsed } from '@sola/utils/relativeTime.ts';

import { Image, ImageSizeIntent } from '../../atoms/image/image.tsx';
import { Blockquote } from '../blockquote/blockquote.tsx';
import {
  FeatureBase,
  FeatureBaseProps,
} from '../feature-base/feature-base.tsx';

export const CardProps = FeatureBaseProps.extend({
  variant: z
    .enum([
      'standard',
      'background',
      'floating',
      'icon',
      'stat',
      'event',
      'post',
      'article',
      'location',
      'listItem',
      'category',
    ])
    .optional(),
  badge: z
    .object({
      text: z.string().optional(),
      color: BadgeColors.optional(),
      size: z.enum(['sm', 'md', 'lg']).optional(),
      iconBefore: IconNames.optional(),
    })
    .optional(),
  hasPlaceholder: z.boolean().optional(),
  onClick: z.function().optional(),
  imageIntent: ImageSizeIntent.optional(),
});
export type CardProps = z.infer<typeof CardProps>;

const DateAndElapsed = ({
  dateTime,
  locale,
}: {
  dateTime: string | Date;
  locale?: string;
}) => {
  const ms = Date.parse(dateTime.toString());
  const elapsed = getElapsed(dateTime, locale);
  return (
    <p className="Card__date label-potent label-lg">
      {elapsed && (
        <>
          <span className="Card__date--elapsed" suppressHydrationWarning>
            {elapsed}
          </span>
          <span suppressHydrationWarning>
            {new Date(ms).toLocaleString(locale, {
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}
          </span>
        </>
      )}
      {!elapsed && 'Invalid Date'}
    </p>
  );
};

export const Card: React.FC<CardProps> = ({
  seoLevel = 3,
  media,
  icon,
  badge,
  variant = 'standard',
  className,
  hasPlaceholder = false,
  displayDate,
  imageIntent,
  ...props
}) => {
  const hasButtons =
    !!props.primaryCTA || !!props.secondaryCTA || !!props.tertiaryCTA;
  const hasContent =
    !!props.title ||
    !!props.subtitle ||
    !!props.body ||
    !!props.quotation ||
    hasButtons;
  const isPost = variant === 'post';
  const isArticle = variant === 'article';
  const isCategory = variant === 'category';
  const isClickable = props.primaryCTA?.link && (isPost || isCategory);

  let Comp = 'div' as React.ElementType;
  let CompProps = {};
  if (isClickable && props.primaryCTA?.link) {
    Comp = Link;
    CompProps = {
      href: props.primaryCTA.link,
      target: props.primaryCTA.target,
      onClick: () => {
        trackClick(
          props.title || 'Unknown',
          props.primaryCTA?.link,
          `Card Card--${variant}`,
          undefined,
          'Link',
        );
        props.onClick?.();
      },
    };
  }

  const hasBaseIcon =
    variant !== 'stat' &&
    variant !== 'event' &&
    variant !== 'article' &&
    variant !== 'category' &&
    (variant === 'icon' || !badge?.text);

  return (
    <Comp
      {...CompProps}
      className={cn(
        `Card Card--${variant}`,
        isCategory ? 'w-full' : 'w-full max-w-xl mx-auto',
        className,
        {
          'no-content': !hasContent,
        },
      )}
    >
      {badge?.text && !isPost && (
        <Badge
          label={badge.text}
          size={badge.size}
          color={badge.color}
          iconBefore={
            badge.iconBefore || (icon && variant !== 'icon') ? icon : undefined
          }
        />
      )}
      {(media?.image || hasPlaceholder) && (
        <div className="Card__media">
          <Image
            image={media?.image}
            className={cn('Card__image')}
            hasPlaceholder
            sizeIntent={imageIntent}
          />
          {hasContent && <div className="Card__media-overlay" />}
        </div>
      )}
      {hasContent && (
        <div className="Card__content">
          {isArticle && !!displayDate && (
            <DateAndElapsed dateTime={displayDate} />
          )}
          <FeatureBase
            seoLevel={seoLevel}
            {...props}
            {...(hasBaseIcon && { icon })}
            {...(variant === 'listItem' && {
              variant: 'listItem',
            })}
            {...(variant === 'stat' && { variant: 'stat', titleSize: 'xl' })}
            {...(variant === 'event' && { variant: 'stat' })}
            {...(variant === 'post' && {
              isClickable: true,
            })}
            {...(variant === 'article' && {
              primaryCTA: undefined,
              secondaryCTA: undefined,
              tertiaryCTA: undefined,
            })}
            {...(variant === 'category' && {
              subtitle: undefined,
              body: props.subtitle || props.body,
              primaryCTA: undefined,
              secondaryCTA: undefined,
              tertiaryCTA: undefined,
              titleSize: 'sm',
            })}
            {...(variant === 'background' &&
              props.quotation?.text && {
                body: (
                  <>
                    {props.body}
                    <div className={cn('Card__quotation')}>
                      <Blockquote quotation={props.quotation} size="lg" />
                    </div>
                  </>
                ),
                quotation: undefined,
              })}
            className={cn(
              {
                'max-w-md': !isCategory,
                'has-checkmarks': variant !== 'background' && !isCategory,
              },
              isCategory && 'w-full',
            )}
            variantSpacing="card"
            alignment="left"
            inline
            handleTitleClick={props.onClick}
          />
          {isCategory && (
            <span
              className="Card__category-icon-slot md:hidden"
              aria-hidden="true"
            >
              {isClickable && (
                <ArkIcon
                  iconName="chevron-right"
                  size="1.5rem"
                  className="Card__category-icon"
                />
              )}
            </span>
          )}
          {badge?.text && isPost && (
            <Badge
              label={badge.text}
              size="lg"
              color={badge.color}
              iconBefore={badge.iconBefore || icon ? icon : undefined}
            />
          )}
        </div>
      )}
    </Comp>
  );
};

export const ArticleCardSkeleton: React.FC = () => {
  return (
    <div className="flex flex-col">
      <div className="mb-comfy-sm">
        <Skeleton
          width="100%"
          height="auto"
          style={{ aspectRatio: '16/9' }}
          borderRadius="0.25rem"
        />
      </div>
      <div className="mb-comfy-sm">
        <Skeleton height={20} width="45%" />
      </div>
      <div className="mb-comfy-sm flex flex-col gap-snug-xs">
        <Skeleton height={28} width="100%" />
        <Skeleton height={28} width="85%" />
      </div>
      <div className="flex flex-col gap-snug-xs">
        <Skeleton height={20} width="100%" />
        <Skeleton height={20} width="95%" />
      </div>
    </div>
  );
};

export const LocationCardSkeleton: React.FC = () => {
  return (
    <div className="flex flex-col">
      <div className="mb-comfy-sm">
        <Skeleton
          width="100%"
          height="auto"
          style={{ aspectRatio: '16/9' }}
          borderRadius="0.25rem"
        />
      </div>
      <div className="mb-comfy-sm">
        <Skeleton height={20} width="45%" />
      </div>
      <div className="mb-comfy-sm flex flex-col gap-snug-xs">
        <Skeleton height={28} width="100%" />
        <Skeleton height={28} width="85%" />
      </div>
      <div className="flex flex-col gap-snug-xs">
        <Skeleton height={20} width="100%" />
        <Skeleton height={20} width="95%" />
      </div>
    </div>
  );
};
